import { useEffect, useState } from 'react';
import { useAccount, usePublicClient } from 'wagmi';
import { contractAddress } from '../_shared/wallet-app/chains';

// Minimal ERC20 ABI for balanceOf.
const ERC20_BALANCE_ABI = [
  {
    name: 'balanceOf',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'a', type: 'address' }],
    outputs: [{ name: 'v', type: 'uint256' }],
  },
];

// useTokenBalance
//
// Read the connected wallet's balance of `symbol` (e.g. "kUSD") on
// `chainId`. Uses wagmi's publicClient.readContract. Refetches when
// chain/account changes; returns null while loading and on error (the UI
// hides the balance line in that case).
//
// `tokenAddressOverride` lets a caller supply the token address directly
// (e.g. from the server-driven GET /bridge/routes response) instead of
// relying on the shared _shared/wallet-app/chains.js STATIC address map —
// that map is stale for Base/Arbitrum kUSD + the bridge contracts (drift-
// guard: money-relevant addresses should come from one live-checked source,
// not a second hardcoded list that can silently diverge from it).

export default function useTokenBalance(chainId, symbol, pollMs = 0, tokenAddressOverride = null) {
  const { address } = useAccount();
  const publicClient = usePublicClient({ chainId: Number(chainId) || undefined });
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    if (!address || !chainId || !symbol || !publicClient) {
      setBalance(null);
      return;
    }
    const tokenAddr = tokenAddressOverride || contractAddress(Number(chainId), symbol);
    if (!tokenAddr) {
      setBalance(null);
      return;
    }

    let cancelled = false;
    let timer = null;

    const tick = async () => {
      try {
        const v = await publicClient.readContract({
          address: tokenAddr,
          abi: ERC20_BALANCE_ABI,
          functionName: 'balanceOf',
          args: [address],
        });
        if (!cancelled) setBalance(BigInt(v));
      } catch (_) {
        if (!cancelled) setBalance(null);
      }
      if (!cancelled && pollMs > 0) {
        timer = setTimeout(tick, pollMs);
      }
    };

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [address, chainId, symbol, publicClient, pollMs, tokenAddressOverride]);

  return balance;
}
